import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { Card, CardBody, Button } from "reactstrap";
import AirHomeLoader from "../../../components/Common/AirHomeLoader";
import { RefreshBtn } from "../../../components/Common/button";
import Request from "../../../helpers/request";
import * as url from "../../../helpers/url_helper";
import { toast } from "react-toastify";
import DeleteSegment from "./DeleteSegment";
import RefreshSegment from "./RefreshSegment";
import { convertProfileLocalDate } from "../../../Utils/configController";

interface SegmentTableProps {
  onToggleAdvancedFilter?: () => void;
  onSelect?: (preset: any) => void;

}

function SegmentTable({ onToggleAdvancedFilter, onSelect }: SegmentTableProps) {
  // Table state
  const [tableData, setTableData] = useState<any>([]);
  const [tablePage, setTablePage] = useState(1);
  const [tablePerPage, setTablePerPage] = useState(10);
  const [tableSearchText, setTableSearchText] = useState("");
  const [tableLoading, setTableLoading] = useState(false);
  const [tableTotalRows, setTableTotalRows] = useState(0);
  const tableFetchingRef = useRef(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSegmentData, setDeleteSegmentData] = useState<any>(null);

  // Refresh modal state
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [refreshSegmentData, setRefreshSegmentData] = useState<any>(null);

  // Refresh key for forcing table refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Action handlers
  const handleEdit = (row: any) => {
    if (onToggleAdvancedFilter && onSelect) {
      onToggleAdvancedFilter();
      onSelect(row);
    }
  };

  const handleRefresh = (row: any) => {
    setRefreshSegmentData(row);
    setRefreshModalOpen(true);
  };

  const handleDelete = (row: any) => {
    setDeleteSegmentData(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleRefreshSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const columns = [
    {
      name: "Name",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "200px" }}>
          <div style={{ fontWeight: "bold" }}>{row.name}</div>
          <div style={{ fontSize: "12px", color: "#6c757d" }}>
            {row.description}
          </div>
        </div>
      ),
    },
    {
      name: "Users Count",
      sortable: false,
      cell: (row: any) => (
        <div className="text-success fw-bold" style={{ cursor: "pointer" }}>
          {row.totalUsersCount || 0}
          {row.percentage > 0 && (
            <span style={{ fontSize: "12px", color: "#6c757d" }}>
              ({row.percentage}%)
            </span>
          )}
        </div>
      ),
    },
    {
      name: "Live Segmentation",
      sortable: false,
      cell: (row: any) => {
        const statusBadge = row?.isLiveSegmentation ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            Live
          </span>
        ) : (
          <span className="font-size-11 badge-soft-warning badge bg-secondary">
            Static
          </span>
        );
        return <div style={{ cursor: "pointer" }}>{statusBadge}</div>;
      },
    },
    {
      name: "Update Cycle",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "120px" }}>
          {row.updateCycle || "-"}
        </div>
      ),
    },
    {
      name: "Last Updated",
      sortable: false,
      cell: (row: any) => (
        <div style={{ cursor: "pointer", minWidth: "120px" }}>
          <div style={{ fontSize: "12px" }}>
            {convertProfileLocalDate(row.lastUpdatedAt) || "-"}
          </div>
        </div>
      ),
    },
    {
      name: "Status",
      sortable: false,
      cell: (row: any) => {
        const statusBadge = row?.isActive ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            Active
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-secondary">
            Inactive
          </span>
        );
        return <div style={{ cursor: "pointer" }}>{statusBadge}</div>;
      },
    },
    {
      name: "Actions",
      sortable: false,
      cell: (row: any) => (
        <div className="d-flex gap-2" style={{ cursor: "pointer" }}>
          <div
            onClick={() => handleRefresh(row)}
            title="Refresh Segment"
            style={{ cursor: "pointer" }}
          >
            <box-icon name="refresh" size="20px" color="#454545"></box-icon>
          </div>
          <div
            onClick={() => handleEdit(row)}
            title="Edit Segment"
            style={{ cursor: "pointer" }}
          >
            <box-icon name="pencil" size="20px" color="#454545"></box-icon>
          </div>

          <div
            onClick={() => handleDelete(row)}
            title="Delete Segment"
            style={{ cursor: "pointer" }}
          >
            <box-icon name="trash" size="20px" color="#454545"></box-icon>
          </div>
        </div>
      ),
    },
  ];

  // Fetch table data on mount and when pagination/search changes
  useEffect(() => {
    const fetchData = async () => {
      setTableLoading(true);
      if (tableFetchingRef.current) {
        return;
      }
      tableFetchingRef.current = true;

      try {
        const reqObj = {
          draw: 1,
          start: (tablePage - 1) * tablePerPage,
          length: tablePerPage,
          search: {
            value: tableSearchText || "",
          },
        };

        const response = await Request.post(
          `${url.BASE_URL}${url.LIST_FILTER_SEGMENT}`,
          reqObj
        );

        setTableData(response.data.data || []);
        setTableTotalRows(response.data.recordsTotal || 0);
      } catch (error: any) {
        console.error("Error fetching data:", error);
        if (error?.response && error.response.status === 401) {
          localStorage.removeItem("userInfo");
          localStorage.removeItem("selectedPremiseID");
          localStorage.removeItem("selectedUserID");
          localStorage.removeItem("selectedUserEmail");
          localStorage.removeItem("Authorization");
          localStorage.removeItem("permissions");
          window.location.reload();
        } else {
          toast.error("Failed to fetch data");
        }
      } finally {
        setTableLoading(false);
        tableFetchingRef.current = false;
      }
    };

    fetchData();
  }, [tablePage, tablePerPage, tableSearchText, refreshKey]);

  return (
    <Card
      style={{
        marginTop: 100,
        marginLeft: 30,
        marginRight: 30,
        marginBottom: 0,
      }}
    >
      <CardBody>
        <div className="">
          <DataTable
            columns={columns}
            data={tableData}
            pagination
            paginationServer
            paginationTotalRows={tableTotalRows}
            onChangePage={(newPage) => setTablePage(newPage)}
            onChangeRowsPerPage={(newPerPage, newPage) => {
              setTablePerPage(newPerPage);
              setTablePage(newPage);
            }}
            highlightOnHover
            customStyles={customStyles}
            paginationRowsPerPageOptions={[10, 25, 50, 100]}
            subHeader
            subHeaderComponent={
              <div className="container-fluid p-0 pb-2">
                <div className="row gx-2 gy-2 align-items-center">
                  <div className="col-12 col-md-6 d-flex flex-wrap align-items-center gap-2">
                    <h5 className="m-0 fw-bold" style={{ fontSize: "18px" }}>
                      {"Segment"}
                    </h5>
                  </div>

                  {/* Search and Action Buttons */}
                  <div className="col-12 col-md-6 d-flex flex-wrap justify-content-md-end align-items-center gap-2">
                    {/* Search Input */}
                    <input
                      type="text"
                      placeholder="Search..."
                      className="form-control form-control-sm"
                      style={{ maxWidth: "150px" }}
                      value={tableSearchText}
                      onChange={(e) => {
                        setTableSearchText(e.target.value);
                        setTablePage(1);
                      }}
                    />

                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => {
                        if (onToggleAdvancedFilter) {
                          onToggleAdvancedFilter();
                        }
                      }}
                    >
                      New Segment
                    </Button>
                  </div>
                </div>
              </div>
            }
          />
        </div>
        {tableLoading && <AirHomeLoader />}
      </CardBody>

      {/* Delete Segment Modal */}
      <DeleteSegment
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        segmentData={deleteSegmentData}
        onDeleteSuccess={handleDeleteSuccess}
      />

      {/* Refresh Segment Modal */}
      <RefreshSegment
        isOpen={refreshModalOpen}
        toggle={() => setRefreshModalOpen(false)}
        segmentData={refreshSegmentData}
        onRefreshSuccess={handleRefreshSuccess}
      />
    </Card>
  );
}

export default SegmentTable;

export const customStyles: any = {
  tableWrapper: {
    style: {
      minWidth: "1100px",
    },
  },
  subHeader: {
    style: {
      padding: "0px",
      margin: "0px",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#EFF2F7",
      fontWeight: "bold",
      fontSize: "14px",
      borderBottom: "none",
    },
  },
  rows: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
  cells: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
};
